import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight } from "lucide-react";
import { IMAGES } from "@/data/content";
import { trackEvent } from "@/lib/analytics";

const LINES = ["Anything", "in the sphere", "is protected."];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.4 } },
};
const lineMask = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 1, ease: [0.16, 1, 0.3, 1] } },
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="top" ref={ref} className="relative min-h-screen overflow-hidden" data-testid="hero-section">
      {/* Background image with parallax */}
      <motion.div style={{ y: imgY }} className="absolute inset-0 -top-24 -bottom-24">
        <img src={IMAGES.serverRoom} alt="Endasphere data center" className="w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#05050A] via-[#05050A]/60 to-[#05050A]" />
      </motion.div>
      <div className="absolute inset-0 bg-grid opacity-60" />
      <div className="absolute inset-0 radial-cyan" />

      <motion.div
        style={{ y: textY, opacity: fade }}
        className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 pt-[180px] lg:pt-[220px] pb-24"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="h-px w-10 bg-[#22D3EE]" />
          <span className="text-xs uppercase tracking-[0.3em] text-[#22D3EE]">
            Global Security & Connectivity
          </span>
        </motion.div>

        <motion.h1
          variants={container}
          initial="hidden"
          animate="show"
          className="font-display font-900 text-white leading-[0.92] tracking-tighter text-5xl sm:text-7xl lg:text-8xl max-w-5xl"
        >
          {LINES.map((line, i) => (
            <span key={i} className="block overflow-hidden py-1">
              <motion.span
                variants={lineMask}
                className={`block ${i === 2 ? "text-[#22D3EE] glow-cyan" : ""}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-10 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed"
        >
          Endasphere is the global network and infrastructure provider that sits between
          your users and origin — accelerating, hardening and protecting websites, apps,
          APIs and players in real time.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.7 }}
          className="mt-12 flex flex-wrap items-center gap-4"
        >
          <a
            href="#contact"
            data-testid="hero-cta-primary"
            onClick={() => trackEvent("cta_click", { location: "hero", label: "Enter the Sphere" })}
            className="group inline-flex items-center gap-2 rounded-full bg-[#22D3EE] px-7 py-4 text-sm font-700 uppercase tracking-[0.12em] text-[#05050A] hover:bg-white transition-colors duration-300"
          >
            Enter the Sphere
            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
          </a>
          <a
            href="#services"
            data-testid="hero-cta-secondary"
            className="inline-flex items-center gap-2 rounded-full border border-white/20 px-7 py-4 text-sm font-500 uppercase tracking-[0.12em] text-white hover:border-[#22D3EE] hover:text-[#22D3EE] transition-colors duration-300"
          >
            Explore Protection
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-[10px] uppercase tracking-[0.3em] text-slate-500"
      >
        Scroll to descend
      </motion.div>
    </section>
  );
}
